import { cx } from '../primitives/cx.js';
import { passthrough } from '../primitives/passthrough.js';
import { Icon } from '../primitives/Icon.tsx';
import { Num } from '../primitives/Num.tsx';

/**
 * ActivityChip - the single next step on a record.
 *
 * One chip, never a list: F-155 allows exactly one open step per record, and a
 * component that could show two would quietly outlive the invariant that makes
 * the queue coherent.
 *
 * The "nothing scheduled" state is the point of the component. A record with
 * no next step must say so, in words, in the place the step would be - a blank
 * there is indistinguishable from a step that failed to load, and both are
 * indistinguishable from nobody having looked.
 */

export interface ActivityChipLabels {
  /** Shown when the record has no open step. Never an em dash. */
  nothingScheduled: string;
  schedule: string;
  overdue: string;
}

export interface ActivityChipProps {
  step:
    | {
        typeLabel: string;
        typeIcon: string;
        /** Already through the formatter: "in 3 days", "yesterday". */
        dueLabel: string;
        overdue: boolean;
        href?: string | undefined;
      }
    | null;
  labels: ActivityChipLabels;
  /** Absent means this reader cannot schedule one, so no control is drawn. */
  onSchedule?: (() => void) | undefined;
  compact?: boolean;
}

export function ActivityChip({
  step,
  labels,
  onSchedule,
  compact,
  ...rest
}: ActivityChipProps & Record<string, unknown>): JSX.Element {
  if (!step) {
    return (
      <span
        {...passthrough(rest)}
        data-component="ActivityChip"
        data-nothing-scheduled
        className="inline-flex items-center gap-2 text-meta text-ink-meta"
      >
        <Icon name="CircleDashed" size="inline" decorative />
        {labels.nothingScheduled}
        {onSchedule ? (
          <button
            type="button"
            data-schedule-step
            onClick={onSchedule}
            className="text-bronze-text underline decoration-bronze-rule underline-offset-4"
          >
            {labels.schedule}
          </button>
        ) : null}
      </span>
    );
  }

  const content = (
    <>
      <Icon name={step.typeIcon} size="inline" decorative />
      <span className="truncate">{step.typeLabel}</span>
      {/* Colour and a word. An overdue chip that is only red says nothing to
          a reader who cannot see red. */}
      <Num className={step.overdue ? 'text-danger-text' : 'text-ink-meta'}>
        {step.overdue ? `${labels.overdue} · ${step.dueLabel}` : step.dueLabel}
      </Num>
    </>
  );

  const className = cx(
    'inline-flex min-w-0 items-center gap-2 rounded-chip px-2 text-meta',
    compact ? 'h-chip' : 'h-touch',
    step.overdue ? 'bg-danger-tint' : 'bg-sunken',
  );

  return step.href ? (
    <a {...passthrough(rest)} data-component="ActivityChip" href={step.href} className={className}>
      {content}
    </a>
  ) : (
    <span {...passthrough(rest)} data-component="ActivityChip" className={className}>
      {content}
    </span>
  );
}
